import { SubscriptionPlan } from '@/constants/pricing';
import { useProductMeta } from './meta/useProductMeta';
import { useLicensePlans } from './useLicensePlans';

export interface LicenseDetails {
  tenant_id: string;
  plan: string;
  status: string;
  expiry_date: string;
  days_remaining: number;
  is_valid: boolean;
  credits: number;
}

export interface LicenseHookResult {
  licenseDetails: LicenseDetails | null;
  isLoading: boolean;
  isProPlan: boolean;
  hasAIFeatures: boolean;
  refreshLicense: () => Promise<void>;
}

export function useLicense(): LicenseHookResult {
  const { tenant, subscription, credits, isLoading } = useProductMeta();
  const { data: plans } = useLicensePlans();

  // Derive license details from tenant data
  let planId = 'trial';

  // Try to get plan from subscription details first (most reliable)
  if (subscription?.plan_details) {
    const dbPlanName = (subscription.plan_details.name || '').toLowerCase();
    // Map DB Plan Names to Frontend IDs
    if (dbPlanName.includes('core') || dbPlanName === 'base') {
      planId = 'core';
    } else if (dbPlanName.includes('plus') || dbPlanName === 'pro') {
      planId = 'plus';
    } else {
      // Fallback: try to find by ID or lowercase name
      planId = subscription.plan_id;
    }
  } else if (subscription?.plan_id) {
    planId = subscription.plan_id;
  }

  // Normalize Plan IDs — handles all known variants:
  //   DB IDs: "core-monthly", "core-yearly", "plus-monthly", "plus-yearly"
  //   Legacy names: "Base", "Focus", "Storyteller", "Pro", "Nexus", "Visionary"
  //   Numeric IDs: 1, 2
  const normalizedId = String(planId).toLowerCase();
  if (normalizedId.startsWith('core') || ['base', '1', 'focus', 'storyteller'].includes(normalizedId)) {
    planId = 'core';
  } else if (normalizedId.startsWith('plus') || ['pro', '2', 'nexus', 'visionary'].includes(normalizedId)) {
    planId = 'plus';
  }

  const foundPlan = plans?.find((p: SubscriptionPlan) => p.id === planId);
  const planName = foundPlan?.name
    || (planId === 'core' ? 'Plot Studio Core' : planId === 'plus' ? 'Plot Studio Plus' : null)
    || subscription?.plan_details?.name
    || 'Trial';

  // Construct license details object
  const licenseDetails: LicenseDetails | null = tenant ? {
    tenant_id: tenant.id,
    plan: planName,
    status: subscription?.status || 'active',
    expiry_date: subscription?.current_period_end || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    days_remaining: subscription?.current_period_end
      ? Math.ceil((new Date(subscription.current_period_end).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
      : 7,
    is_valid: true,
    credits: credits // Added credits field
  } : null;

  // AI features are available on trial and plus plans, NOT on core
  const hasAIFeatures = planId !== 'core';

  return {
    licenseDetails,
    isLoading,
    isProPlan: planId === 'plus', // Plus is the new Pro
    hasAIFeatures,
    refreshLicense: async () => {
      // Invalidate queries if needed, for now just a placeholder
      return Promise.resolve();
    }
  };
}
